import api from "./api";
import { capitalizeFirstLetter } from "../utils/capitalize";

const hasAnyFilter = (body) => {
  if (!body) return false;
  return [
    body.memberIds,
    body.providerNpis,
    body.pharmacyNpis,
    body.programIds,
  ].some((arr) => Array.isArray(arr) && arr.length);
};

const isSearchQuery = (search) => !!search?.trim();

const buildPagination = (queryParams, disabled) => {
  if (disabled) return {};
  const page = Number.isInteger(queryParams.page) ? queryParams.page : 0;
  const size = Number.isInteger(queryParams.size) ? queryParams.size : 30;
  return { page, size };
};

const buildFilterQueryParts = (body) => {
  if (!body) return {};
  const parts = {};
  if (Array.isArray(body.memberIds) && body.memberIds.length)
    parts.memberIds = body.memberIds.join(",");
  if (Array.isArray(body.providerNpis) && body.providerNpis.length)
    parts.providerIds = body.providerNpis.join(",");
  if (Array.isArray(body.pharmacyNpis) && body.pharmacyNpis.length)
    parts.pharmacyIds = body.pharmacyNpis.join(",");
  if (Array.isArray(body.programIds) && body.programIds.length)
    parts.programIds = body.programIds.join(",");
  return parts;
};

export const fetchManualTasks = async (params = {}) => {
  try {
    const { body: requestBody = {}, ...queryParams } = params;
    const hasFilterPayload = hasAnyFilter(requestBody);
    const isSearch = isSearchQuery(queryParams.search);
    const disablePagination = isSearch || hasFilterPayload;

    const { page, size, ...rest } = queryParams;
    const finalQueryParams = {
      outreachType: queryParams.outreachType,
      ...(queryParams.memberBadge && { memberBadge: queryParams.memberBadge }),
      ...rest,
      ...buildFilterQueryParts(requestBody),
      ...buildPagination(queryParams, disablePagination),
    };

    const response = await api.get(`/api/manual-tasks`, {
      params: finalQueryParams,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (Array.isArray(response.data.content)) {
      const mappedContent = response.data.content
        .map((item) => ({
          ...item,
          name: item.name ? capitalizeFirstLetter(item.name) : item.name,
        }))
        .map((item) => ({
          id: item.id,
          recipient: {
            name: item.recipientDisplayName,
            type: item.recipientType,
            language: item.language,
            timezone: item.timezone,
            id: item.recipientId || item.id,
          },
          recentActivity: {
            status: item.recentActivityLabel,
            timestamp: item.recentActivityTime,
          },
          attempts: item.numberOfAttempts,
          outreachReason: item.outreachReason,
          taskNote: item.note,
          dueDate: item.dueDate,
          linkedMember: item.linkedMemberName,
          recipientId: item.recipientId,
        }));

      return {
        content: mappedContent,
        totalElements: response.data.totalElements || mappedContent.length,
      };
    }

    return { content: [], totalElements: 0 };
  } catch (e) {
    return `fetchPriorityOutreach failed: ${e.message}`;
  }
};

export const searchMembers = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "MEMBER",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",

        "ngrok-skip-browser-warning": "true",
      },
    });

    if (!Array.isArray(response.data)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .map((item) => {
          let name = "";
          if (item.member?.name) name = item.member.name;
          else if (item.name) name = item.name;
          else if (item.memberName) name = item.memberName;
          else return null;

          return capitalizeFirstLetter(name);
        })
        .filter(Boolean);
    }

    return results;
  } catch (error) {
    return `searchMembers failed: ${error.message}`;
  }
};

export const searchProviders = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "PROVIDER",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (Array.isArray(response.data?.content)) {
      return response.data.content.map((item) => ({
        ...item,
        name: item.name ? capitalizeFirstLetter(item.name) : item.name,
      }));
    }

    if (!Array.isArray(response.data)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .map((item) => {
          let name = "";
          if (item.provider?.name) name = item.provider.name;
          else if (item.name) name = item.name;
          else if (item.providerName) name = item.providerName;
          else return null;

          return capitalizeFirstLetter(name);
        })
        .filter(Boolean);

      results = [...new Set(results)];
    }

    return results;
  } catch (error) {
    return `searchProviders failed: ${error.message}`;
  }
};

export const searchPharmacies = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "PHARMACY",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (!Array.isArray(response.data.content)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .map((item) => {
          let name = "";
          if (item.pharmacy?.name) name = item.pharmacy.name;
          else if (item.name) name = item.name;
          else if (item.pharmacyName) name = item.pharmacyName;
          else return null;

          return capitalizeFirstLetter(name);
        })
        .filter(Boolean);

      results = [...new Set(results)];
    }

    return results;
  } catch (error) {
    return `searchPharmacies failed: ${error.message}`;
  }
};

export const searchPrograms = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "PROGRAM",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (!Array.isArray(response.data.content)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .flatMap((item) => {
          let name = "";
          if (Array.isArray(item.programs))
            return item.programs.map((p) => capitalizeFirstLetter(p));
          if (item.program?.name) name = item.program.name;
          else if (item.name) name = item.name;
          else if (item.programName) name = item.programName;
          else return [];

          return [capitalizeFirstLetter(name)];
        })
        .filter(Boolean);

      results = [...new Set(results)];
    }

    return results;
  } catch (error) {
    return `searchPrograms failed: ${error.message}`;
  }
};

export const getFilterOptions = async () => {
  try {
    const response = await fetchManualTasks({ size: 30 });

    if (!Array.isArray(response)) {
      return {
        members: [],
        providers: [],
        pharmacies: [],
        programs: [],
      };
    }

    const members = [
      ...new Set(
        response
          .filter((item) => item.member?.name)
          .map((item) => item.member.name)
          .filter(Boolean)
      ),
    ];

    const providers = [
      ...new Set(
        response
          .filter((item) => item.provider?.name)
          .map((item) => item.provider.name)
          .filter(Boolean)
      ),
    ];

    const pharmacies = [
      ...new Set(
        response
          .filter((item) => item.pharmacy?.name)
          .map((item) => item.pharmacy.name)
          .filter(Boolean)
      ),
    ];

    const programs = [
      ...new Set(
        response.flatMap((item) => item.programs || []).filter(Boolean)
      ),
    ];

    return {
      members,
      providers,
      pharmacies,
      programs,
    };
  } catch (error) {
    return `getFilterOptions failed: ${error.message}`;
  }
};

export const updateMemberPersonalInfo = async (memberId, personalInfo) => {
  try {
    const response = await api.patch(
      `/outreach/members/${memberId}/personal-info`,
      {
        allergy: Array.isArray(personalInfo.allergies)
          ? personalInfo.allergies.join(", ")
          : personalInfo.allergies,
        language: personalInfo.language,
        timezone: personalInfo.timeZone,

        gender: personalInfo.gender,
        dob: personalInfo.dateOfBirth,
      }
    );
    return response.data;
  } catch (error) {
    return `updateMemberPersonalInfo failed: ${error.message}`;
  }
};

export const fetchMemberOverview = async (memberId) => {
  try {
    const response = await api.get(`/outreach/members/${memberId}`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data) {
      const memberData = response.data;
      const currentYear = new Date().getFullYear();
      const birthYear = memberData.dob
        ? new Date(memberData.dob).getFullYear()
        : null;
      const age = birthYear ? currentYear - birthYear : null;
      const phone =
        memberData.phoneNumber || memberData.preferredPhoneNum || "";

      return {
        name: `${capitalizeFirstLetter(memberData.fullName || "")}`.trim(),
        sex: memberData.sex || "",
        gender: memberData.gender || "",
        age: age,
        dob: memberData.dob || "",

        phone: phone,
        preferredPhoneNum: memberData.preferredPhoneNum || "",
        preferredPhoneNumber: memberData.preferredPhoneNumber || "",

        contact: phone,
        email: memberData.email || "",
        timeZone: memberData.timezone || "",
        memberId: memberData.id ? memberData.id.toString() : "",
        personCode: memberData.personCode || "",
        status: "Active",
        language: memberData.language || "",
        address: {
          street: memberData.address || "",
          city: memberData.city || "",
          state: memberData.state || "",
          zip: memberData.postalCode || memberData.zip || "",
          fullAddress: `${memberData.address || ""}, ${memberData.city || ""
            }, ${memberData.state || ""} ${memberData.postalCode || memberData.zip || ""
            }`.trim(),
        },
        carrier: memberData.carrier || "",
        group: memberData.group || "",
        subscriberNum: memberData.subscriberNum || "",
        allergies: memberData.allergy ? [memberData.allergy] : [],

        notes: [],
        dispositions: [],
        doNotCall: memberData.doNotCall || false,
      };
    }
  } catch (e) {
    return `fetchPriorityOutreach failed: ${e.message}`;
  }
};

export const searchRecipients = async (name, type = "members") => {
  try {
    const response = await api.get(`/recipients/${type}/search`, {
      params: {
        name: name,
      },
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data;
  } catch (error) {
    return `searchRecipients failed: ${error.message}`;
  }
};

export const searchRecipientMembers = async (name) => {
  return searchRecipients(name, "members");
};

export const searchRecipientProviders = async (name) => {
  return searchRecipients(name, "providers");
};
export const searchRecipientPharmacies = async (name) => {
  return searchRecipients(name, "pharmacies");
};

export const createManualTask = async (taskData) => {
  try {
    const response = await api.post(`/api/manual-tasks`, taskData, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data;
  } catch (error) {
    return `createManualTask failed: ${error.message}`;
  }
};

export const completeManualTask = async (taskData) => {
  try {
    const response = await api.post(
      `/api/manual-tasks/member/dispositions`,
      taskData,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );

    return response.data;
  } catch (error) {
    return `completeManualTask failed: ${error.message}`;
  }
};

export const deleteManualTask = async (taskIds) => {
  try {
    const ids = Array.isArray(taskIds) ? taskIds : [taskIds];
    const idsString = ids.join(",");

    const response = await api.delete(
      `/api/manual-tasks/batch?ids=${idsString}`,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );

    return response.data;
  } catch (error) {
    return `deleteManualTask failed: ${error.message}`;
  }
};

export const fetchLinkedMembers = async (recipientType, recipientId) => {
  try {
    const type = recipientType;

    const response = await api.get(
      `/recipients/${type}/${recipientId}/members`,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );

    return response.data;
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchMemberPrograms = async (memberId) => {
  try {
    const response = await api.get(`/recipients/members/${memberId}/programs`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data;
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};
export const fetchRecipientDetails = async (recipientId, type = "members") => {
  try {
    const response = await api.get(`/recipients/${type}/${recipientId}`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data;
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const updateManualTask = async (taskId, taskData) => {
  try {
    const response = await api.patch(`/api/manual-tasks/${taskId}`, taskData, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data;
  } catch (error) {
    return `updateManualTask failed: ${error.message}`;
  }
};

export const fetchRecipientPrograms = async (recipientId, recipientType) => {
  try {
    let endpoint;

    if (recipientType === "member") {
      endpoint = `/recipients/members/${recipientId}`;
    } else if (recipientType === "provider" || recipientType === "pharmacy") {
      endpoint = `/recipients/members/${recipientId}`;
    } else {
      return `Unsupported recipient type: ${recipientType}`;
    }

    const response = await api.get(endpoint, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    return response.data;
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchPharmacyDetails = async (pharmacyId) => {
  try {
    const response = await api.get(`/recipients/pharmacies/${pharmacyId}`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });
    return response.data;
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};

export const fetchProviderDetails = async (providerId) => {
  try {
    const response = await api.get(`/recipients/providers/${providerId}`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });
    return response.data;
  } catch (error) {
    return `fetchPriorityOutreach failed: ${error.message}`;
  }
};
